/**
 * PayPal on the app's side: FMS staff link each farm to the PayPal subscription it already pays
 * FMS by (found from PayPal's own payments), see whose payments need a look, and choose which
 * PayPal plan new farms subscribe to; owners see how their farm is paid for and can start a
 * monthly PayPal subscription themselves. Nothing here charges, cancels or switches anything off.
 */
import { useEffect, useState } from "react";
import { isPaying, legacyText, paymentProblem, type LegacyPrice } from "../../../../packages/core/src/payment.ts";
import { api, ApiError, type Account, type FoundPayPal, type Payment, type PayPalSubscription, type Plan } from "../api.ts";
import { clock } from "../format.ts";
import { go } from "../route.ts";
import { Problem, Section, Spinner, useLoad, useNow } from "../ui.tsx";

const msg = (e: unknown) => (e instanceof ApiError || e instanceof Error ? e.message : "Something went wrong.");
const day = (ms: number) => new Date(ms).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });

/** PayPal's reference kinds, in words, for "Kinds seen". */
const KIND: Record<string, string> = { SUB: "subscription", PAP: "recurring payment", ODR: "order", TXN: "payment", none: "other" };

const STATUS: Record<string, [string, string]> = {
  ACTIVE: ["good", "Paying"],
  APPROVED: ["info", "Approved"],
  APPROVAL_PENDING: ["warn", "Not approved yet"],
  SUSPENDED: ["crit", "Paused by PayPal"],
  CANCELLED: ["crit", "Cancelled"],
  EXPIRED: ["crit", "Ended"],
  NO_RECENT_PAYMENT: ["crit", "No payment lately"],
};

export function PayPalStatus({ status }: { status: string }) {
  const [tone, text] = STATUS[status] ?? ["info", status];
  return <span className={`pill ${tone}`}>{text}</span>;
}

/** Last and next payment, failed payments, who pays. */
function PaymentFacts({ p, now, staff }: { p: Payment | PayPalSubscription; now: number; staff: boolean }) {
  return (
    <dl className="kv small">
      <div>
        <dt>Status</dt>
        <dd>
          <PayPalStatus status={p.status} />
        </dd>
      </div>
      {p.lastPayment && (
        <div>
          <dt>Last payment</dt>
          <dd>
            ${p.lastPayment.amount} {p.lastPayment.currency} on {day(p.lastPayment.at)}
          </dd>
        </div>
      )}
      {p.nextBillingAt !== undefined && (
        <div>
          <dt>Next payment</dt>
          <dd>{day(p.nextBillingAt)}</dd>
        </div>
      )}
      {p.failedPayments > 0 && (
        <div>
          <dt>Failed payments</dt>
          <dd style={{ color: "var(--crit)" }}>{p.failedPayments}</dd>
        </div>
      )}
      {(p.payerName || p.payerEmail) && (
        <div>
          <dt>Paid by</dt>
          <dd>{[p.payerName, p.payerEmail].filter(Boolean).join(" · ")}</dd>
        </div>
      )}
      {staff && (
        <div>
          <dt>PayPal</dt>
          <dd className="mono">
            {"subscriptionId" in p ? p.subscriptionId : p.id} · {"source" in p && p.source === "profile" ? "older Subscribe button, checked from its payments" : `plan ${p.planId}`}
            {"checkedAt" in p ? ` · checked ${clock(p.checkedAt, now)}` : ""}
          </dd>
        </div>
      )}
    </dl>
  );
}

/** FMS staff, on a customer's page: the PayPal subscriptions that pay for the account (usually one). */
export function PayPalCard({ a, onDone }: { a: Account; onDone: () => void }) {
  const now = useNow(60_000);
  const [id, setId] = useState("");
  const [label, setLabel] = useState("");
  const [adding, setAdding] = useState(false);
  const [preview, setPreview] = useState<{ subscription: PayPalSubscription; linkedTo?: { accountId: string; name: string } } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const run = async (f: () => Promise<unknown>) => {
    setBusy(true);
    setError("");
    try {
      await f();
    } catch (e) {
      setError(msg(e));
    } finally {
      setBusy(false);
    }
  };
  const list = a.payments ?? [];
  const showForm = adding || !list.length;
  return (
    <section className="card pad" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <h3 style={{ fontSize: 17 }}>PayPal</h3>
      {list.map((p) => {
        const problem = paymentProblem(p, now);
        return (
          <div key={p.subscriptionId} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {(list.length > 1 || p.label) && <b className="small">{p.label ?? p.subscriptionId}</b>}
            {problem && <div className="banner warn small">{problem}. The farm stays on: decide with the customer what to do.</div>}
            <PaymentFacts p={p} now={now} staff />
            {p.legacy && <span className="pill warn" style={{ alignSelf: "flex-start" }}>FMS-Control price (grace period)</span>}
            <div className="toolbar">
              <button className="btn ghost" disabled={busy} onClick={() => void run(async () => (await api.markPaypalLegacy(a.id, p.subscriptionId, !p.legacy), onDone()))}>
                {p.legacy ? "Now on the plan's price" : "Still on the FMS-Control price"}
              </button>
              <button
                className="btn ghost"
                disabled={busy}
                onClick={() =>
                  confirm(`Unlink ${p.subscriptionId}? It keeps charging in PayPal: cancel it there if that's what's wanted.`) && void run(async () => (await api.unlinkPaypal(a.id, p.subscriptionId), onDone()))
                }
              >
                Unlink
              </button>
            </div>
          </div>
        );
      })}
      {list.length > 0 && (
        <div className="toolbar">
          <button className="btn" disabled={busy} onClick={() => void run(async () => (await api.refreshPaypal(a.id), onDone()))}>
            Check with PayPal now
          </button>
          {!adding && (
            <button className="btn ghost" onClick={() => setAdding(true)}>
              Link another subscription…
            </button>
          )}
        </div>
      )}
      {showForm && (
        <>
          <p className="muted small" style={{ margin: 0 }}>
            {list.length ? "Another subscription this customer pays (e.g. one per farm from FMS-Control)." : "Not linked to a PayPal subscription."} Find it on the{" "}
            <button className="linkish" onClick={() => go({ name: "paypal" })}>
              PayPal page
            </button>{" "}
            or type its ID (I-…, in PayPal under the payment's details).
          </p>
          <form
            className="toolbar"
            onSubmit={(e) => {
              e.preventDefault();
              void run(async () => setPreview(await api.paypalSubscription(id.trim().toUpperCase())));
            }}
          >
            <input value={id} onChange={(e) => (setId(e.target.value), setPreview(null))} placeholder="I-ABC123DEF456" aria-label="PayPal subscription ID" className="mono" style={{ flex: 1, minWidth: 0 }} />
            <button className="btn" disabled={busy || !id.trim()}>
              Check
            </button>
          </form>
          {preview && (
            <div className="card pad" style={{ background: "var(--surface-2)", display: "flex", flexDirection: "column", gap: 8 }}>
              <PaymentFacts p={preview.subscription} now={now} staff />
              {preview.linkedTo ? (
                <div className="banner warn small">Already linked to {preview.linkedTo.name}.</div>
              ) : (
                <>
                  {list.length > 0 && (
                    <div className="field">
                      <label htmlFor="ppLabel">What it pays for (optional)</label>
                      <input id="ppLabel" value={label} onChange={(e) => setLabel(e.target.value.slice(0, 60))} placeholder="e.g. Back block" />
                    </div>
                  )}
                  <div>
                    <button
                      className="btn primary"
                      disabled={busy}
                      onClick={() => void run(async () => (await api.linkPaypal(a.id, preview.subscription.id, label.trim() || undefined), setPreview(null), setId(""), setLabel(""), setAdding(false), onDone()))}
                    >
                      Link to {a.name}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}
      {error && <div className="error small">{error}</div>}
    </section>
  );
}

/** FMS staff: PayPal connection, plans for new subscriptions, problems, and finding existing subscriptions. */
export function PayPalPage() {
  const data = useLoad(() => api.paypal(), []);
  const accounts = useLoad(() => api.accounts().then((r) => r.accounts), []);
  if (data.error && !data.data) return <Problem error={data.error} onRetry={data.reload} />;
  if (!data.data) return <Spinner />;
  const d = data.data;
  if (!d.configured)
    return (
      <Section title="PayPal">
        <div className="card pad" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <b>PayPal isn't connected to this server yet.</b>
          <p className="muted small" style={{ margin: 0 }}>
            It needs the FMS PayPal app's client ID and secret in the Key Vault (paypal-client-id, paypal-client-secret), and the webhook's ID (paypal-webhook-id). Then
            restart the server. See docs/deploy-azure.md → PayPal.
          </p>
        </div>
      </Section>
    );
  return (
    <>
      <Section title="PayPal" right={<span className={`pill ${d.env === "live" ? "good" : "warn"}`}>{d.env === "live" ? "Live" : "Sandbox (test money)"}</span>}>
        <div className="tiles">
          <div className="tile">
            <div className="lbl">Farms linked</div>
            <div className="big num">{d.linked}</div>
          </div>
          <div className="tile">
            <div className="lbl">Need a look</div>
            <div className="big num" style={d.problems.length ? { color: "var(--crit)" } : undefined}>
              {d.problems.length}
            </div>
          </div>
        </div>
        {d.problems.length > 0 && (
          <div className="card list">
            {d.problems.map((p) => (
              <div key={p.subscriptionId} className="alert">
                <span className="sev crit" />
                <div>
                  <h3>
                    <button className="linkish" onClick={() => go({ name: "account", id: p.accountId })}>
                      {p.name}
                    </button>
                  </h3>
                  <p>{p.problem}</p>
                </div>
                <span />
              </div>
            ))}
          </div>
        )}
      </Section>
      <OldPrice legacy={d.legacy} onLegacy={d.onLegacy} onSaved={data.reload} />
      <FindSubscriptions accounts={accounts.data ?? []} onLinked={data.reload} />
      <PlansForNewFarms plans={d.plans} onSaved={data.reload} />
      <p className="muted small">Each linked farm is checked with PayPal every 6 hours, and straight away when PayPal sends a notification.</p>
    </>
  );
}

/** The old FMS-Control price existing subscribers keep, and when that grace period ends. */
function OldPrice({ legacy, onLegacy, onSaved }: { legacy: LegacyPrice | null; onLegacy: number; onSaved: () => void }) {
  const iso = (ms: number) => new Date(ms).toISOString().slice(0, 10);
  const [price, setPrice] = useState(String(legacy?.price ?? 30));
  const [until, setUntil] = useState(iso(legacy?.until ?? Date.now() + 182 * 86_400_000));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  return (
    <Section title="Old FMS-Control price">
      <p className="muted small" style={{ margin: 0 }}>
        Customers who already pay through PayPal keep paying what they did in FMS-Control for a grace period, then move to their plan's price. Linking a subscription they already
        had marks it as on the old price. {onLegacy} farm{onLegacy === 1 ? "" : "s"} on it now. Customers see the end date on Admin → Plan. Nothing changes in PayPal by itself:
        moving someone to the new price is done in PayPal, and they have to approve it.
      </p>
      <form
        className="card pad toolbar"
        onSubmit={(e) => {
          e.preventDefault();
          setBusy(true);
          setError("");
          setSaved(false);
          api
            .setPaypalLegacy(Number(price), new Date(`${until}T23:59:59`).getTime())
            .then(() => (setSaved(true), onSaved()), (err) => setError(msg(err)))
            .finally(() => setBusy(false));
        }}
      >
        <div className="field">
          <label htmlFor="oldPrice">Price a month ($)</label>
          <input id="oldPrice" type="number" min="1" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} style={{ width: 110 }} />
        </div>
        <div className="field">
          <label htmlFor="oldUntil">Kept until</label>
          <input id="oldUntil" type="date" value={until} onChange={(e) => setUntil(e.target.value)} />
        </div>
        <button className="btn primary" disabled={busy} style={{ alignSelf: "flex-end" }}>
          {legacy ? "Save" : "Set the grace period"}
        </button>
        {saved && <span className="small muted" style={{ alignSelf: "center" }}>Saved.</span>}
        {!legacy && <span className="small muted" style={{ alignSelf: "center" }}>Not set yet: 6 months from today is filled in.</span>}
      </form>
      {error && <div className="error small">{error}</div>}
    </Section>
  );
}

function FindSubscriptions({ accounts, onLinked }: { accounts: Account[]; onLinked: () => void }) {
  const [days, setDays] = useState(90);
  const [list, setList] = useState<FoundPayPal[] | null>(null);
  const [extra, setExtra] = useState<{ regularPayers: Array<{ payerName?: string; payerEmail?: string; payments: number; lastAmount: string; currency: string; lastPaidAt: number }>; scanned: { transactions: number; received: number; byType: Record<string, number> } } | null>(null);
  const [pick, setPick] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const find = async () => {
    setBusy(true);
    setError("");
    try {
      const r = await api.findPaypal(days);
      setList(r.subscriptions);
      setExtra({ regularPayers: r.regularPayers, scanned: r.scanned });
      setPick(Object.fromEntries(r.subscriptions.flatMap((s) => (s.suggested ? [[s.subscriptionId, s.suggested.accountId]] : []))));
    } catch (e) {
      setError(msg(e));
    } finally {
      setBusy(false);
    }
  };
  const link = async (s: FoundPayPal) => {
    setBusy(true);
    setError("");
    try {
      const acc = accounts.find((a) => a.id === pick[s.subscriptionId])!;
      await api.linkPaypal(acc.id, s.subscriptionId);
      setList((l) => l?.map((x) => (x.subscriptionId === s.subscriptionId ? { ...x, linkedTo: { accountId: acc.id, name: acc.name } } : x)) ?? null);
      onLinked();
    } catch (e) {
      setError(msg(e));
    } finally {
      setBusy(false);
    }
  };
  const open = list?.filter((s) => !s.linkedTo).length ?? 0;
  return (
    <Section title="Existing subscriptions">
      <p className="muted small" style={{ margin: 0 }}>
        Customers who already pay FMS through PayPal. They're found from PayPal's payments, so each shows when it last paid. Link each to its farm: nothing changes for the
        customer, and PayPal keeps charging as before.
      </p>
      <div className="toolbar">
        <label className="small" htmlFor="ppDays">
          Payments in the last
        </label>
        <select id="ppDays" value={days} onChange={(e) => setDays(Number(e.target.value))}>
          {[45, 90, 180, 365].map((n) => (
            <option key={n} value={n}>
              {n} days
            </option>
          ))}
        </select>
        <button className="btn primary" disabled={busy} onClick={() => void find()}>
          {busy && !list ? "Looking in PayPal…" : "Find in PayPal"}
        </button>
        {list && <span className="small muted">{list.length} found, {open} not linked</span>}
      </div>
      {error && <div className="error small">{error}</div>}
      {extra && (
        <p className="small muted" style={{ margin: 0 }}>
          Looked at {extra.scanned.transactions} PayPal transactions in the last {days} days: {extra.scanned.received} money in, {list?.length ?? 0} subscription
          {list?.length === 1 ? "" : "s"}.
          {!list?.length && extra.scanned.transactions > 0 && (
            <> Kinds seen: {Object.entries(extra.scanned.byType).map(([k, n]) => `${n} ${KIND[k] ?? k}`).join(", ")}.</>
          )}
          {extra.scanned.transactions === 0 && " PayPal returned nothing: Transaction search can take a few hours to start working for a new app, and PayPal adds payments to it up to 3 hours late."}
        </p>
      )}
      {list && list.length > 0 && (
        <div className="card tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Paid by</th>
                <th>Last payment</th>
                <th>Farm</th>
              </tr>
            </thead>
            <tbody>
              {list.map((s) => (
                <tr key={s.subscriptionId}>
                  <td>
                    <b>{s.payerName ?? "—"}</b>
                    <div className="small muted">{s.payerEmail}</div>
                    <div className="small mono">{s.subscriptionId}</div>
                  </td>
                  <td className="small">
                    ${s.lastAmount} {s.currency}
                    <div className="muted">
                      {day(s.lastPaidAt)} · {s.payments} payment{s.payments === 1 ? "" : "s"}
                    </div>
                  </td>
                  <td>
                    {s.linkedTo ? (
                      <button className="linkish" onClick={() => go({ name: "account", id: s.linkedTo!.accountId })}>
                        ✓ {s.linkedTo.name}
                      </button>
                    ) : (
                      <div className="toolbar" style={{ flexWrap: "nowrap" }}>
                        <select value={pick[s.subscriptionId] ?? ""} onChange={(e) => setPick({ ...pick, [s.subscriptionId]: e.target.value })} aria-label={`Farm for ${s.subscriptionId}`}>
                          <option value="">Choose the farm…</option>
                          {[...accounts]
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((a) => (
                              <option key={a.id} value={a.id}>
                                {a.name}
                                {a.payments?.length ? ` (${a.payments.length} linked)` : ""}
                              </option>
                            ))}
                        </select>
                        <button className="btn" disabled={busy || !pick[s.subscriptionId]} onClick={() => void link(s)}>
                          Link
                        </button>
                      </div>
                    )}
                    {s.suggested && !s.linkedTo && pick[s.subscriptionId] === s.suggested.accountId && <div className="small muted">Suggested: {s.suggested.why}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {extra && extra.regularPayers.length > 0 && (
        <>
          <b className="small">Paid more than once without a subscription</b>
          <p className="muted small" style={{ margin: 0 }}>
            These people paid FMS by PayPal more than once in the period, but not through a PayPal subscription (an invoice, a payment button, or money sent). They can't be linked;
            if they should be paying monthly, they can start a subscription from Admin → Plan once their farm is live.
          </p>
          <div className="card tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Paid by</th>
                  <th>Last payment</th>
                </tr>
              </thead>
              <tbody>
                {extra.regularPayers.map((p) => (
                  <tr key={(p.payerEmail ?? "") + (p.payerName ?? "")}>
                    <td>
                      <b>{p.payerName ?? "—"}</b>
                      <div className="small muted">{p.payerEmail}</div>
                    </td>
                    <td className="small">
                      ${p.lastAmount} {p.currency}
                      <div className="muted">
                        {day(p.lastPaidAt)} · {p.payments} payments
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Section>
  );
}

function PlansForNewFarms({ plans, onSaved }: { plans: Array<{ plan: Plan; name: string; price: number | null; planId?: string; paypal?: { name: string; status: string; price?: { amount: string; currency: string; every: string } }; problem?: string }>; onSaved: () => void }) {
  const [ids, setIds] = useState<Record<string, string>>(() => Object.fromEntries(plans.map((p) => [p.plan, p.planId ?? ""])));
  useEffect(() => setIds(Object.fromEntries(plans.map((p) => [p.plan, p.planId ?? ""]))), [plans]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  return (
    <Section title="PayPal plans for new farms">
      <p className="muted small" style={{ margin: 0 }}>
        When a farm's owner pays online (Admin → Plan → Pay with PayPal), PayPal charges them the plan set here. Make the plans in PayPal (Pay &amp; Get Paid → Subscriptions → Plans,
        monthly, AUD) and paste each plan's ID (P-…). Leave a plan empty to keep it off.
      </p>
      <form
        className="card pad"
        style={{ display: "flex", flexDirection: "column", gap: 10 }}
        onSubmit={(e) => {
          e.preventDefault();
          setBusy(true);
          setError("");
          api
            .setPaypalPlans(ids as Partial<Record<Plan, string>>)
            .then(onSaved, (err) => setError(msg(err)))
            .finally(() => setBusy(false));
        }}
      >
        {plans.map((p) => (
          <div key={p.plan} className="field">
            <label htmlFor={`pp-${p.plan}`}>
              {p.name} {p.price !== null ? `($${p.price} a month in Think-Control)` : ""}
            </label>
            <input id={`pp-${p.plan}`} className="mono" value={ids[p.plan] ?? ""} onChange={(e) => setIds({ ...ids, [p.plan]: e.target.value })} placeholder="P-…" />
            {p.paypal && (
              <span className="small muted">
                PayPal: {p.paypal.name}
                {p.paypal.price ? ` · $${p.paypal.price.amount} ${p.paypal.price.currency} every ${p.paypal.price.every.toLowerCase()}` : ""} · {p.paypal.status}
              </span>
            )}
            {p.problem && <span className="error small">{p.problem}</span>}
          </div>
        ))}
        {error && <div className="error small">{error}</div>}
        <div>
          <button className="btn primary" disabled={busy}>
            {busy ? "Checking with PayPal…" : "Save"}
          </button>
        </div>
      </form>
    </Section>
  );
}

/** Admin → Plan: how the farm is paid for, and paying online with PayPal. */
export function OwnerPayment({
  payments,
  payOnline,
  owner,
  free,
  accountId,
  legacy,
}: {
  payments: Payment[];
  payOnline: boolean;
  owner: boolean;
  free: boolean;
  accountId?: string;
  legacy?: LegacyPrice | null;
}) {
  const now = useNow(60_000);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  if (free) return null;
  const active = payments.filter(isPaying);
  const start = async () => {
    setBusy(true);
    setError("");
    try {
      const r = await api.paypalSubscribe(accountId);
      location.href = r.approveUrl;
    } catch (e) {
      setError(msg(e));
      setBusy(false);
    }
  };
  if (!payments.length && !payOnline) return null;
  return (
    <div className="card pad" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <b>Payment</b>
      {payments.map((p) => {
        const problem = paymentProblem(p, now);
        return (
          <div key={p.subscriptionId} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {payments.length > 1 && p.label && <b className="small">{p.label}</b>}
            {problem && <div className="banner warn small">{problem}. Please check your PayPal account, or call FMS on 1300 003 367.</div>}
            <PaymentFacts p={p} now={now} staff={false} />
            {p.legacy && legacy && (
              <div className="banner info small">
                {legacyText(legacy, now)}. {legacy.until > now ? "After that your plan's price applies: FMS will be in touch before anything changes." : "FMS will be in touch about moving to your plan's price."}
              </div>
            )}
          </div>
        );
      })}
      {payments.length > 0 && (
        <p className="muted small" style={{ margin: 0 }}>
          Paid monthly through PayPal. To change the card or bank account, or to stop paying, go to your PayPal account → Payments → Automatic payments.
        </p>
      )}
      {!active.length &&
        payOnline &&
        (owner ? (
          <div>
            <button className="btn primary" disabled={busy} onClick={() => void start()}>
              {busy ? "Opening PayPal…" : "Pay monthly with PayPal"}
            </button>
            <p className="muted small" style={{ margin: "6px 0 0" }}>
              You'll approve it on PayPal's page, then come straight back here. You can cancel any time in PayPal.
            </p>
          </div>
        ) : (
          <p className="muted small" style={{ margin: 0 }}>
            The farm's owner can set up payment here.
          </p>
        ))}
      {error && <div className="error small">{error}</div>}
    </div>
  );
}

/**
 * Back from PayPal's page (?paypal=done&subscription_id=I-…): link the subscription to the farm,
 * tidy the address, and show Admin → Plan with how it went.
 */
export function usePayPalReturn(accountId: string | undefined): string | null {
  const [note, setNote] = useState<string | null>(null);
  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    const state = qs.get("paypal");
    if (!state) return;
    const id = qs.get("subscription_id");
    history.replaceState(null, "", `${location.pathname}#/admin/plan`);
    go({ name: "admin", tab: "plan" });
    if (state !== "done" || !id) {
      setNote("PayPal was closed before the subscription was approved. Nothing has been charged.");
      return;
    }
    setNote("Checking with PayPal…");
    api.paypalConfirm(id, accountId).then(
      (r) => setNote(r.payment.status === "ACTIVE" ? "Thanks: the farm is now paid monthly through PayPal." : "PayPal has the subscription; it will show as paying once PayPal confirms the first payment."),
      (e) => setNote(`PayPal approved it, but we couldn't link it: ${msg(e)} Please call FMS on 1300 003 367.`),
    );
    // Once, when the app opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return note;
}
